import { NobaAssistant, useNobaAssistantRef } from "@banknorwegian/noba-assistant-chat";
import { BNButton, BNThemeMode, RegionId, useQueryTanStack, useStatePersisted } from "@bn-monorepo/js-packages";
import AssistantIcon from "@mui/icons-material/Assistant";
import { ComponentProps, createContext, PropsWithChildren, ReactNode, useContext, useEffect, useState } from "react";

const API_BASE = "/proxychat";

type NobaAssistantLocale = NonNullable<ComponentProps<typeof NobaAssistant>["locale"]>;

const REGION_TO_LOCALE: Record<RegionId, NobaAssistantLocale> = {
  [RegionId.None]: "no",
  [RegionId.Norway]: "no",
  [RegionId.Sweden]: "se",
  [RegionId.Denmark]: "dk",
  [RegionId.Finland]: "fi",
  [RegionId.Germany]: "de",
  [RegionId.Spain]: "es",
};

const norwegianDisclaimer = "Bank Norwegian chat er AI-generert";
const swedishDisclaimer = "Bank Norwegian chatten är AI-genererad";
const LOCALE_TO_DISCLAIMER: Record<NobaAssistantLocale, string> = {
  no: norwegianDisclaimer,
  nb: norwegianDisclaimer,
  se: swedishDisclaimer,
  sv: swedishDisclaimer,
  dk: "Bank Norwegian chatten er AI-genereret",
  fi: "Bank Norwegian -chat toimii tekoälyn avulla",
  de: "Der Chat von Bank Norwegian ist KI-generiert",
  es: "El chat de Bank Norwegian está generado por IA",
};

interface IChatContext {
  sendMessage: (content: string) => void;
}

const ChatContext = createContext<IChatContext | null>(null);

export const useChatContext = () => useContext(ChatContext);

function ChatProviderContent({
  children,
  regionId,
  alternativeChat,
  alternativeSendMessage,
  disabled,
  disableAlternative,
  allowDevTools,
}: PropsWithChildren<{
  regionId: RegionId;
  alternativeChat?: ReactNode;
  alternativeSendMessage?: (content: string) => void;
  disabled?: boolean;
  disableAlternative?: boolean;
  allowDevTools?: boolean;
}>) {
  const [show, setShow] = useStatePersisted(null, "show-chat", false, true, "boolean");
  const locale = REGION_TO_LOCALE[regionId];

  const isEnabled = useQueryTanStack<boolean>(`${API_BASE}/chat/isenabled/`, {
    staleTime: 5 * 60 * 1000,
    enabled: !disabled,
  });

  const [messageAfterLoad, setMessageAfterLoad] = useState<undefined | string>();

  const assistantRef = useNobaAssistantRef();

  // Dark mode
  const { mode } = useContext(BNThemeMode);

  // Select which chat to render
  const shouldRenderNobaAssistant = !disabled && isEnabled.data;
  const shouldRenderAlternative =
    !shouldRenderNobaAssistant && (disabled || !isEnabled.isLoading) && !disableAlternative;

  const sendMessage = (content: string) => {
    if (shouldRenderNobaAssistant) {
      setShow(true);
      const assistant = assistantRef.current;
      if (assistant) {
        void assistant.sendMessage(content);
        return;
      }

      setMessageAfterLoad(content);
    } else if (shouldRenderAlternative) {
      alternativeSendMessage?.(content);
    } else {
      setMessageAfterLoad(content);
    }
  };

  // Send pending message after loading `isEnabled`
  useEffect(() => {
    if (!messageAfterLoad) return;

    if (shouldRenderAlternative) {
      alternativeSendMessage?.(messageAfterLoad);
      setMessageAfterLoad(undefined);
      return;
    }

    if (!shouldRenderNobaAssistant) return;

    setShow(true);

    const intervalId = setInterval(() => {
      const assistant = assistantRef.current;
      if (!assistant) return;

      void assistant.sendMessage(messageAfterLoad);
      setMessageAfterLoad(undefined);
      clearInterval(intervalId);
    }, 100);

    return () => {
      clearInterval(intervalId);
    };
  }, [assistantRef, alternativeSendMessage, messageAfterLoad, shouldRenderNobaAssistant, shouldRenderAlternative]);

  // Render NOBA Assistant?
  return (
    <ChatContext.Provider value={{ sendMessage }}>
      {children}
      {shouldRenderNobaAssistant ? (
        <>
          <BNButton
            onClick={() => setShow((prevState) => !prevState)}
            data-uit="noba-chat-toggle"
            sx={{
              position: "fixed",
              bottom: { xs: "6.4rem", md: "1.2rem" },
              right: { xs: "0.6rem", md: "1.2rem" },
              zIndex: 5000,
              maxWidth: "initial",
              minWidth: "initial",
              width: 44,
              height: 44,
              px: 0,
              py: 0,
              borderRadius: "999.9rem",
            }}
          >
            <AssistantIcon sx={{ width: 20, height: 20 }} />
          </BNButton>
          <div
            // Prevent MUI focus management interference
            onFocus={(e) => {
              e.stopPropagation();
            }}
            data-uit="noba-chat-container"
            style={{ height: "0rem", width: "0rem", overflow: "visible" }}
          >
            <NobaAssistant
              hidden={!show}
              onRequestClose={() => setShow(false)}
              company="bano"
              title="Bank Norwegian chat"
              disclaimer={LOCALE_TO_DISCLAIMER[locale] || LOCALE_TO_DISCLAIMER["no"]}
              apiUrl={API_BASE}
              position="bottom-right"
              mobilePosition="bottom"
              locale={locale}
              ref={assistantRef}
              allowDevTools={allowDevTools}
              mode={mode}
              style={{ zIndex: 5040 }}
            />
          </div>
        </>
      ) : shouldRenderAlternative ? (
        alternativeChat
      ) : undefined}
    </ChatContext.Provider>
  );
}

export function ChatProvider(props: ComponentProps<typeof ChatProviderContent>) {
  return <ChatProviderContent {...props} />;
}
